import React, { useState, useMemo, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { FiInfo, FiPlus } from 'react-icons/fi';
import AssessmentPopup from './AssessmentPopup';
import { useAssessmentContext } from '../AssessmentContext';
import { useTests } from '../../../hooks/useTests';
import { normalizeIntroVideosList } from '../../../utils/assessmentStudent';
import TestsDataTable from './TestsDataTable';
import CreateTestPanel from './CreateTestPanel';
import BatchWizard from '../Wizard/BatchWizard';
import './assessment.css';
import '../assessment-module-mobile.css';

import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../contexts/UserContext';
import testService from '../../../services/testService';

const STEPS = [
  { id: 'create', label: 'Create Test', path: '/workflow/create' },
  { id: 'question-bank', label: 'Question Bank', path: '/workflow/question-bank' },
  { id: 'template', label: 'Template', path: '/workflow/template' },
  { id: 'groups', label: 'Groups & Assign', path: '/workflow/groups' },
  { id: 'publish', label: 'Save', path: '/workflow/publish' }
];

const loadDraft = () => {
  const draftStr = localStorage.getItem('draft_assessment');
  if (draftStr) {
    try {
      return JSON.parse(draftStr);
    } catch (e) {
      console.error('Failed to parse draft_assessment', e);
    }
  }
  return null;
};

const saveDraft = (a) => {
  localStorage.setItem('draft_assessment', JSON.stringify(a));
  window.dispatchEvent(new Event('draft-updated'));
};

const clearDraft = () => {
  localStorage.removeItem('draft_assessment');
  window.dispatchEvent(new Event('draft-updated'));
};

const generateRandomCode = () => `TST-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

const newAssessment = () => ({
  id: generateRandomCode(),
  name: '',
  category: 'Internal',
  type: '',
  instructions: '',
  videoEnabled: false,
  videoFiles: [],
  navigationControl: false,
  autoSubmit: false,
  fullscreenMode: false,
  tabSwitchWarning: false,
  status: 'Draft',
  currentStep: 'create',
  completedSteps: [],
  questionBank: null,
  template: null,
  groups: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'internal', label: 'Internal', dbValue: 'Enova' },
  { value: 'external', label: 'External', dbValue: 'HR Recruitment-Fresher' },
];

const TestDashboard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { setActiveModule, navigateToModule } = useAssessmentContext();
  const { tests, loading, refreshTests, updateTest, deleteTest, removeTestPack } = useTests();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [assignedCount, setAssignedCount] = useState(0);

  const show_my_tests_btn = user && Number(user.user_id || user.id) === 121;

  useEffect(() => {
    if (user && show_my_tests_btn) {
      const fetchCount = async () => {
        try {
          const res = await testService.getStudentAssessments();
          if (res.status === 'success' && Array.isArray(res.assessments)) {
            const pendingTests = res.assessments.filter(t => t.submission_status !== 'COMPLETED');
            setAssignedCount(pendingTests.length);
          }
        } catch (e) {
          console.error('Failed to fetch assigned tests count', e);
        }
      };
      fetchCount();
      const interval = setInterval(fetchCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user, show_my_tests_btn]);
  const [editingTest, setEditingTest] = useState(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });

  const [localDraftsList, setLocalDraftsList] = useState([]);
  const [localPublished, setLocalPublished] = useState([]);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  const loadLocalTests = () => {
    const drafts = [];

    // 1. Load active draft
    const draftStr = localStorage.getItem('draft_assessment');
    if (draftStr) {
      try {
        const parsed = JSON.parse(draftStr);
        if (parsed && parsed.name) {
          if (!parsed.createdAt && !parsed.created_at) {
            parsed.createdAt = new Date().toISOString();
            parsed.created_at = parsed.createdAt;
            localStorage.setItem('draft_assessment', JSON.stringify(parsed));
          }
          const creationDate = parsed.createdAt || parsed.created_at;
          drafts.push({
            ...parsed,
            id: parsed.id || 'draft-assessment',
            isActiveDraft: true,
            isLocalDraft: true,
            isLocal: true,
            status: 'Draft',
            createdAt: creationDate,
            created_at: creationDate
          });
        }
      } catch (e) {
        console.error(e);
      }
    }

    // 2. Load saved drafts
    const savedDraftsStr = localStorage.getItem('saved_drafts');
    if (savedDraftsStr) {
      try {
        const parsed = JSON.parse(savedDraftsStr);
        if (Array.isArray(parsed)) {
          let updatedSaved = false;
          parsed.forEach(d => {
            if (d && d.name) {
              if (!d.createdAt && !d.created_at) {
                d.createdAt = new Date().toISOString();
                d.created_at = d.createdAt;
                updatedSaved = true;
              }
              const creationDate = d.createdAt || d.created_at;
              drafts.push({
                ...d,
                isSavedDraft: true,
                isLocalDraft: true,
                isLocal: true,
                status: 'Draft',
                createdAt: creationDate,
                created_at: creationDate
              });
            }
          });
          if (updatedSaved) {
            localStorage.setItem('saved_drafts', JSON.stringify(parsed));
          }
        }
      } catch (e) {
        console.error(e);
      }
    }

    setLocalDraftsList(drafts);

    // 3. Load published tests
    const pubStr = localStorage.getItem('published_tests');
    if (pubStr) {
      try {
        const parsed = JSON.parse(pubStr);
        if (Array.isArray(parsed)) {
          let updatedPub = false;
          const mapped = parsed.map(t => {
            if (!t.createdAt && !t.created_at) {
              t.createdAt = new Date().toISOString();
              t.created_at = t.createdAt;
              updatedPub = true;
            }
            const creationDate = t.createdAt || t.created_at;
            return {
              ...t,
              id: 'pub-' + t.id,
              isLocalPublished: true,
              isLocal: true,
              status: 'Published',
              createdAt: creationDate,
              created_at: creationDate
            };
          });
          setLocalPublished(mapped);
          if (updatedPub) {
            localStorage.setItem('published_tests', JSON.stringify(parsed));
          }
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      setLocalPublished([]);
    }
  };

  useEffect(() => {
    const sync = () => {
      setDraft(loadDraft());
      loadLocalTests();
    };
    sync();
    window.addEventListener("draft-updated", sync);
    window.addEventListener("storage", sync);

    // Silent background refresh every 30s — keeps the DB test list current
    // without requiring a manual page reload after another admin creates/publishes a test
    const pollInterval = setInterval(() => {
      if (!document.hidden) {
        refreshTests({ showLoading: false });
      }
    }, 30000);

    return () => {
      window.removeEventListener("draft-updated", sync);
      window.removeEventListener("storage", sync);
      clearInterval(pollInterval);
    };
  }, [refreshTests]);

  useEffect(() => {
    const showReminder = localStorage.getItem('show_publish_reminder_popup');
    if (showReminder === 'true') {
      localStorage.removeItem('show_publish_reminder_popup');
      setPopupConfig({
        isOpen: true,
        type: 'info',
        title: 'Publish Test',
        message: 'Your test is saved. Open the accordion and publish the test.',
        confirmText: 'OK',
        showCancel: false,
        showCloseButton: false,
        closeOnBackdrop: false,
        onConfirm: () => {
          setPopupConfig({ isOpen: false });
        }
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateNew = () => {
    const existing = loadDraft();
    if (existing && existing.name) {
      setDraft(existing);
      setResumeOpen(true);
      return;
    }
    if (existing) clearDraft();
    const a = newAssessment();
    saveDraft(a);
    navigate("/workflow/create");
  };

  const resumeDraft = (testToResume, forceStep1 = false) => {
    setResumeOpen(false);
    const targetDraft = (testToResume && typeof testToResume === 'object' && 'id' in testToResume) ? testToResume : draft;
    if (!targetDraft) return;

    const activeDraft = loadDraft();
    if (targetDraft.isSavedDraft) {
      const savedDraftsStr = localStorage.getItem('saved_drafts') || '[]';
      try {
        let savedDrafts = JSON.parse(savedDraftsStr);
        savedDrafts = savedDrafts.filter(d => d.id !== targetDraft.id);
        if (activeDraft && activeDraft.name && activeDraft.id !== targetDraft.id) {
          savedDrafts.unshift(activeDraft);
        }
        localStorage.setItem('saved_drafts', JSON.stringify(savedDrafts));
      } catch (e) {
        console.error(e);
      }
    }

    const cleanTest = { ...targetDraft };
    if (String(cleanTest.id).startsWith('pub-')) {
      cleanTest.id = cleanTest.id.replace('pub-', '');
    }
    const creationDate = cleanTest.createdAt || cleanTest.created_at || new Date().toISOString();
    delete cleanTest.isSavedDraft;
    delete cleanTest.isLocalDraft;
    delete cleanTest.isLocal;
    delete cleanTest.status;
    delete cleanTest.created_at;
    delete cleanTest.isActiveDraft;
    cleanTest.createdAt = creationDate;
    cleanTest.created_at = creationDate;

    const effectiveForceStep1 = Boolean(forceStep1);

    if (effectiveForceStep1) {
      cleanTest.currentStep = STEPS[0].id;
    }

    saveDraft(cleanTest);
    const targetStepId = cleanTest.currentStep || STEPS[0].id;
    const step = (effectiveForceStep1 ? STEPS[0] : STEPS.find(s => s.id === targetStepId)) ?? STEPS[0];
    navigate(step.path);
  };

  const startFresh = () => {
    const existing = loadDraft();
    if (existing && existing.name) {
      const savedDraftsStr = localStorage.getItem('saved_drafts') || '[]';
      try {
        const savedDrafts = JSON.parse(savedDraftsStr);
        const filtered = savedDrafts.filter(d => d.id !== existing.id);
        filtered.unshift(existing);
        localStorage.setItem('saved_drafts', JSON.stringify(filtered));
      } catch (e) {
        console.error('Failed to save draft to saved_drafts', e);
      }
    }
    clearDraft();
    const a = newAssessment();
    saveDraft(a);
    setResumeOpen(false);
    navigate("/workflow/create");
  };

  const handleResumeDraftClick = (test) => {
    if (!test.isLocal && test.status === 'Draft') {
      handleEditTest(test, false);
      return;
    }
    setDraft(test);
    setResumeOpen(true);
  };


  const handleEditTest = (test, forceStep1 = false) => {
    const rawPacks = test.isLocal ? (test.groups || []) : (test.test_packs || []);
    const hasGroups = rawPacks.length > 0;
    const isEffectivelyPublished = test.completed_count > 0 || rawPacks.some(p => String(p.status || '').toLowerCase() === 'published');
    
    if (test.isLocalDraft) {
      setDraft(test);
      resumeDraft(test, forceStep1);
    } else if (test.isLocalPublished && !isEffectivelyPublished) {
      const draftToResume = {
        ...test,
        ...(test.backendId ? { backendDraftId: test.backendId } : {})
      };
      setDraft(draftToResume);
      resumeDraft(draftToResume, forceStep1);
    } else if (!test.isLocal && test.status === 'Draft' && !isEffectivelyPublished) {
      // DB Draft — recovered after cache clear or from a different device.
      
      // 1. Try to find a matching rich local draft
      let richDraft = null;
      const activeDraft = loadDraft();
      if (activeDraft && (String(activeDraft.backendDraftId) === String(test.id) || String(activeDraft.id) === String(test.id))) {
        richDraft = activeDraft;
      } else {
        const savedDraftsStr = localStorage.getItem('saved_drafts');
        if (savedDraftsStr) {
          try {
            const savedDrafts = JSON.parse(savedDraftsStr);
            richDraft = savedDrafts.find(d => String(d.backendDraftId) === String(test.id) || String(d.id) === String(test.id));
          } catch(e) {}
        }
      }

      if (richDraft) {
        setDraft(richDraft);
        saveDraft(richDraft);
        toast.success('Draft restored from local workspace!');
        resumeDraft(richDraft, forceStep1);
        return;
      }

      // Park any existing active draft first before we overwrite it with DB rebuilt draft
      if (activeDraft && activeDraft.name) {
        const savedDraftsStr = localStorage.getItem('saved_drafts') || '[]';
        try {
          let savedDrafts = JSON.parse(savedDraftsStr);
          savedDrafts = savedDrafts.filter(d => d.id !== activeDraft.id);
          savedDrafts.unshift(activeDraft);
          localStorage.setItem('saved_drafts', JSON.stringify(savedDrafts));
        } catch (e) {
          console.error(e);
        }
      }
      
      const mappedGroups = (test.test_packs || []).map(pack => ({
        id: pack.id,
        backendPackId: pack.id,
        pack_name: pack.pack_name,
        template_id: pack.template_id,
        template_name: pack.template?.name || pack.template_name || '',
        user_role: pack.user_role ? pack.user_role.split(',') : [],
        candidates: pack.candidates || '',
        scheduled_date: pack.scheduled_date,
        start_time: pack.start_time,
        end_time: pack.end_time,
        duration: pack.duration || 60,
        status: pack.status || 'Draft',
      }));

      const firstPackWithTemplate = (test.test_packs || []).find(p => p.template);
      let restoredTemplate = null;
      let restoredBank = null;
      
      if (firstPackWithTemplate && firstPackWithTemplate.template) {
         const tpl = firstPackWithTemplate.template;
         const normalizedSections = (tpl.sections || []).map((sec) => {
           const count = Number(sec.question_count ?? sec.count ?? sec.num_questions ?? 0);
           const marks = Number(sec.marks_each ?? sec.marks ?? sec.marks_per_question ?? 0);
           const name = sec.section_name || sec.name || 'Section';
           return {
             ...sec,
             name,
             section_name: name,
             count,
             marks,
             question_count: count,
             marks_each: marks,
             num_questions: count,
             marks_per_question: marks,
           };
         });
         restoredTemplate = {
            id: tpl.id,
            name: tpl.name,
            questionBank: tpl.bank || '',
            passMark: Number(tpl.pass_mark ?? tpl.passMark ?? 0),
            sections: normalizedSections,
         };
      } else if (test.template_id) {
         restoredTemplate = {
            id: test.template_id,
            name: '',
            questionBank: test.question_bank || '',
            passMark: 0,
            sections: []
         };
      }

      const qbName = test.question_bank || restoredTemplate?.questionBank || (firstPackWithTemplate && firstPackWithTemplate.template && firstPackWithTemplate.template.bank);
      if (qbName) {
         restoredBank = { name: qbName };
      }

      // Reconstruct a wizard-compatible draft from the DB record
      const restoredDraft = {
        id:               String(test.id),
        backendDraftId:   test.id,
        name:             test.name || '',
        category:         test.category || 'Enova',
        type:             test.assessment_type || '',
        instructions:     test.instructions || test.description || '',
        videoEnabled:     test.add_video == 1 || test.add_video === 'Yes',
        videoFiles:       normalizeIntroVideosList(test.intro_videos),
        navigationControl: test.allow_backtracking == 1,
        autoSubmit:       test.show_results == 1,
        fullscreenMode:   test.proctored_exam == 1,
        tabSwitchWarning: test.browser_lockdown == 1,
        status:           'Draft',
        currentStep:      mappedGroups.length > 0 ? 'publish' : (restoredTemplate ? 'groups' : 'create'),
        completedSteps:   mappedGroups.length > 0 ? ['create', 'question-bank', 'template', 'groups'] : (restoredTemplate ? ['create', 'question-bank', 'template'] : ['create']),
        groups:           mappedGroups,
        template:         restoredTemplate,
        questionBank:     restoredBank,
        isEditMode:       true,
        createdAt:        test.created_at || test.createdAt || new Date().toISOString(),
        created_at:       test.created_at || test.createdAt || new Date().toISOString(),
      };
      saveDraft(restoredDraft);
      toast.success('Draft restored! Continue editing your test.');
      resumeDraft(restoredDraft, forceStep1);
    } else {
      setEditingTest(test);
      setIsCreateOpen(true);
    }
  };


  const handleDeleteTest = (test) => {
    if (test.isLocalDraft) {
      setPopupConfig({
        isOpen: true,
        type: 'warning',
        title: 'Delete draft test?',
        message: 'Are you sure you want to delete this draft test? This action cannot be undone.',
        confirmText: 'Yes, delete it!',
        onConfirm: () => {
          if (test.isSavedDraft) {
            const savedDraftsStr = localStorage.getItem('saved_drafts') || '[]';
            try {
              const savedDrafts = JSON.parse(savedDraftsStr);
              const next = savedDrafts.filter(d => d.id !== test.id);
              localStorage.setItem('saved_drafts', JSON.stringify(next));
            } catch (e) {
              console.error(e);
            }
            window.dispatchEvent(new Event('draft-updated'));
          } else {
            clearDraft();
          }
          setPopupConfig({ isOpen: false });
          toast.success('Draft test deleted.');
          loadLocalTests();
        }
      });
    } else if (test.isLocalPublished) {
      setPopupConfig({
        isOpen: true,
        type: 'warning',
        title: 'Delete published test?',
        message: 'Are you sure you want to delete this published test? This action cannot be undone.',
        confirmText: 'Yes, delete it!',
        onConfirm: () => {
          const pubStr = localStorage.getItem('published_tests');
          if (pubStr) {
            try {
              const parsed = JSON.parse(pubStr);
              const next = parsed.filter(t => 'pub-' + t.id !== test.id);
              localStorage.setItem('published_tests', JSON.stringify(next));
              toast.success('Published test deleted.');
              loadLocalTests();
            } catch (e) {
              console.error(e);
            }
          }
          setPopupConfig({ isOpen: false });
          loadLocalTests();
        }
      });
    } else {
      setPopupConfig({
        isOpen: true,
        type: 'warning',
        title: 'Delete this test?',
        message: 'Are you sure you want to delete this test? This action cannot be undone.',
        confirmText: 'Yes, delete it!',
        onConfirm: async () => {
          try {
            await deleteTest(test.id);

            // Clean up from local active draft if it matches
            const activeDraftStr = localStorage.getItem('draft_assessment');
            if (activeDraftStr) {
              try {
                const activeDraft = JSON.parse(activeDraftStr);
                if (String(activeDraft.backendDraftId) === String(test.id) || String(activeDraft.id) === String(test.id)) {
                  clearDraft();
                }
              } catch(e) {}
            }

            // Clean up from local saved drafts if it matches
            const savedDraftsStr = localStorage.getItem('saved_drafts');
            if (savedDraftsStr) {
              try {
                const savedDrafts = JSON.parse(savedDraftsStr);
                const next = savedDrafts.filter(d => String(d.backendDraftId) !== String(test.id) && String(d.id) !== String(test.id));
                localStorage.setItem('saved_drafts', JSON.stringify(next));
              } catch(e) {}
            }

            // Clean up from local published tests if it was synced
            const pubStr = localStorage.getItem('published_tests');
            if (pubStr) {
              try {
                const parsed = JSON.parse(pubStr);
                const next = parsed.filter(t => String(t.backendId) !== String(test.id));
                localStorage.setItem('published_tests', JSON.stringify(next));
              } catch (e) {
                console.error(e);
              }
            }

            setPopupConfig({ isOpen: false });
            toast.success('The test has been removed.');
            loadLocalTests();
            refreshTests();
          } catch (err) {
            console.error(err);
            setPopupConfig({ isOpen: false });
            toast.error('Failed to delete test');
          }
        }
      });
    }
  };

  const handleClosePanel = () => {
    setIsCreateOpen(false);
    setEditingTest(null);
  };

  const handleSuccessfulSave = () => {
    handleClosePanel();
    refreshTests();
  };

  const handleInitializeBatch = (assessment) => {
    setSelectedAssessment(assessment);
    setIsWizardOpen(true);
  };

  const activeCategoryFilter = CATEGORY_FILTER_OPTIONS.find((opt) => opt.value === categoryFilter);

  const allTestsCombined = useMemo(() => {
    const dbList = Array.isArray(tests) ? tests : [];
    const dbIds = new Set(dbList.map(t => String(t.id)));

    const dbNames = new Set(dbList.map(t => (t.name || '').trim().toLowerCase()));

    // Filter localDraftsList to exclude any that have already been synced (backendDraftId matches a db ID or id matches a db ID)
    const filteredLocalDrafts = (localDraftsList || []).filter(t => {
      if (t.backendDraftId && dbIds.has(String(t.backendDraftId))) {
        return false;
      }
      if (t.id && dbIds.has(String(t.id))) {
        return false;
      }
      if (t.name && dbNames.has(t.name.trim().toLowerCase())) {
        return false;
      }
      return true;
    });

    // Filter localPublished to exclude any that have already been synced (backendId matches a db ID)
    const filteredLocalPublished = (localPublished || []).filter(t => {
      if (t.backendId && dbIds.has(String(t.backendId))) {
        return false;
      }
      if (t.name && dbNames.has(t.name.trim().toLowerCase())) {
        return false;
      }
      return true;
    });

    const list = dbList.map(dbTest => {
      if (dbTest.status === 'Draft') {
        const matchingDraft = (localDraftsList || []).find(d => String(d.backendDraftId) === String(dbTest.id) || String(d.id) === String(dbTest.id));
        if (matchingDraft && matchingDraft.videoFiles) {
          dbTest.videoFiles = matchingDraft.videoFiles;
        }
      }

      const matchingLocal = (localPublished || []).find(t => String(t.backendId) === String(dbTest.id));
      if (matchingLocal && matchingLocal.groups) {
        const dbPackIds = new Set((dbTest.test_packs || []).map(p => String(p.id)));
        const localUnpublishedGroups = matchingLocal.groups.filter(g => !g.backendPackId && !dbPackIds.has(String(g.id)));
        return {
          ...dbTest,
          test_packs: [...(dbTest.test_packs || []), ...localUnpublishedGroups]
        };
      }
      return dbTest;
    });
    const draftIds = new Set(filteredLocalDrafts.map(t => String(t.id).replace('pub-', '')));
    const trulyFilteredLocalPublished = filteredLocalPublished.filter(t => {
      const rawId = String(t.id).replace('pub-', '');
      if (draftIds.has(rawId)) return false;
      return true;
    });

    if (filteredLocalDrafts && filteredLocalDrafts.length > 0) {
      list.unshift(...filteredLocalDrafts);
    }
    if (trulyFilteredLocalPublished.length > 0) {
      list.unshift(...trulyFilteredLocalPublished);
    }
    
    return list;
  }, [tests, localDraftsList, localPublished]);

  const filteredTests = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return (allTestsCombined || []).filter((t) => {
      const name = t.name || '';
      const category = t.category || '';
      const categoryLabel =
        category === 'Enova'
          ? 'internal'
          : category === 'HR Recruitment-Fresher'
            ? 'external'
            : category.toLowerCase();
      const matchesSearch =
        !query ||
        name.toLowerCase().includes(query) ||
        category.toLowerCase().includes(query) ||
        categoryLabel.includes(query);
      const matchesCategory =
        !activeCategoryFilter?.dbValue || category === activeCategoryFilter.dbValue;
      return matchesSearch && matchesCategory;
    });
  }, [allTestsCombined, searchQuery, categoryFilter, activeCategoryFilter]);

  return (
    <div className="assess-mod-page assess-mod-inventory-page p-6 min-h-0 w-full">
      <div className="assess-mod-inventory-header assess-mod-header flex items-center justify-between mb-8">
        <div className="assess-mod-header-brand">
          <h3 className="text-2xl font-bold text-slate-800">Learning Management System</h3>
        </div>
        <div className="assess-mod-inventory-actions assess-mod-actions-row flex gap-3">
          <button
            id="btn_new_test_toggle"
            className={`btn-assessment btn-assessment-danger ${isCreateOpen ? 'active' : ''}`}
            onClick={handleCreateNew}
          >
            <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] transition-colors ${isCreateOpen ? 'bg-red-800' : 'bg-white/20'}`}>
              1
            </span>
            <span>Create New Test</span>
          </button>
          <button
            className="btn-assessment btn-assessment-danger"
            onClick={() => setActiveModule('templates')}
          >
            <span className="flex items-center justify-center w-5 h-5 bg-white/20 rounded-full text-[10px]">
              <FiPlus />
            </span>
            <span>Template Master</span>
          </button>
          {show_my_tests_btn && (
            <button
              className="btn-assessment btn-assessment-danger"
              onClick={() => {
                localStorage.setItem('assessment_view_as_student', 'true');
                window.dispatchEvent(new Event('assessment_view_mode_change'));
              }}
            >
              <span className="flex items-center justify-center w-5 h-5 bg-white/20 rounded-full text-[10px] relative">
                <i className="bi bi-journal-check"></i>
                {assignedCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[8px] font-black w-4 h-4 flex items-center justify-center rounded-full animate-pulse shadow-sm border border-white">
                    {assignedCount}
                  </span>
                )}
              </span>
              <span>My Tests</span>
            </button>
          )}
        </div>
      </div>

      <CreateTestPanel
        isOpen={isCreateOpen}
        onClose={handleClosePanel}
        editingTest={editingTest}
        onSuccess={handleSuccessfulSave}
      />

      <BatchWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        assessment={selectedAssessment}
      />

      <div className="assess-mod-inventory-card assess-mod-card bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 overflow-visible mb-12">
        <div className="assess-mod-inventory-toolbar assess-mod-tabs-row px-8 py-6 border-b border-slate-100 bg-slate-100 flex items-center justify-end">
          <div className="assess-mod-inventory-filters flex items-center gap-4">
            <div className="relative" ref={categoryDropdownRef}>
              <button
                type="button"
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="flex items-center gap-3 bg-white h-10 px-4 rounded-xl border border-slate-100 shadow-sm hover:border-red-200 transition-all min-w-[160px]"
              >
                <i className="bi bi-filter text-slate-400 text-sm"></i>
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wide flex-1 text-left">
                  {activeCategoryFilter?.label || 'All'}
                </span>
                <i className={`bi bi-chevron-down text-xs text-slate-400 transition-transform duration-300 ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isCategoryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl z-[100] py-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
                  {CATEGORY_FILTER_OPTIONS.map((option) => (
                    <div
                      key={option.value || 'all'}
                      onClick={() => {
                        setCategoryFilter(option.value);
                        setIsCategoryDropdownOpen(false);
                      }}
                      className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wide cursor-pointer transition-all flex items-center gap-2 ${categoryFilter === option.value
                          ? 'bg-red-50 text-red-600'
                          : 'text-slate-500 hover:bg-slate-50 hover:text-red-500'
                        }`}
                    >
                      <i className={`bi ${option.value === 'internal' ? 'bi-building-fill' :
                          option.value === 'external' ? 'bi-globe2' :
                            'bi-grid-fill'
                        } text-[10px]`}></i>
                      <span>{option.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search Input */}
            <div className="flex items-center gap-3 bg-white h-10 px-4 rounded-xl border border-slate-100 shadow-sm w-[320px] focus-within:border-red-200 transition-all">
              <i className="bi bi-search text-slate-400 text-sm"></i>
              <input
                type="text"
                className="text-xs font-bold text-slate-600 border-0 focus:ring-0 p-0 w-full placeholder:text-slate-400 uppercase tracking-wide outline-none bg-transparent"
                placeholder="Search by name or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Result Analysis Button */}
            <button
              id="btn_result_analysis"
              onClick={() => {
                sessionStorage.removeItem('analytics_show_group_compare');
                sessionStorage.removeItem('analytics_show_question_perf');
                navigateToModule('analytics', 'inventory');
              }}
              className="flex items-center gap-2 px-4 h-10 bg-[#B91C1C] text-white rounded-xl text-xs font-bold shadow-md hover:bg-[#991B1B] shadow-red-100/30 transition-all cursor-pointer border-0 outline-none"
            >
              <i className="bi bi-bar-chart-fill text-sm"></i>
              <span>Result Analysis</span>
            </button>
          </div>
        </div>
        <div className="assess-mod-inventory-table-wrap assess-mod-table-wrap p-0">
          {loading ? (
            <div className="py-32 flex flex-col items-center justify-center gap-6">
              <div className="w-14 h-14 border-[4px] border-slate-100 border-t-red-600 rounded-full animate-spin"></div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Synchronizing Inventory...</p>
            </div>
          ) : (
            <TestsDataTable
              data={filteredTests}
              onEdit={handleEditTest}
              onDelete={handleDeleteTest}
              refreshData={refreshTests}
              onPackDeleted={removeTestPack}
              onResumeDraftClick={handleResumeDraftClick}
            />
          )}
        </div>
      </div>
      <AssessmentPopup
        {...popupConfig}
        onCancel={popupConfig.onCancel || (() => setPopupConfig({ ...popupConfig, isOpen: false }))}
      />

      {resumeOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl border border-slate-100 animate-in fade-in zoom-in duration-300 relative p-6 text-center">
            {/* Close Button */}
            <button
              type="button"
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors bg-transparent border-0 cursor-pointer p-0"
              onClick={() => setResumeOpen(false)}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Info Icon */}
            <div className="w-12 h-12 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center text-xl mx-auto mb-4 border border-rose-100/50">
              <FiInfo />
            </div>

            <h3 className="text-lg font-black text-slate-800 tracking-tight mb-2">Resume Draft Test?</h3>
            <p className="text-xs text-slate-500 font-bold leading-relaxed mb-6">
              You have an unfinished test: <span className="text-rose-600 font-extrabold">{draft?.name}</span>. Would you like to resume where you left off, or start a new one?
            </p>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={startFresh}
                className="btn-assessment btn-assessment-secondary flex items-center justify-center border border-slate-200 text-slate-600 hover:bg-slate-50 !bg-white !text-slate-700 hover:translate-y-[-1px] active:translate-y-0"
              >
                Create New Test
              </button>
              <button
                onClick={() => resumeDraft()}
                className="btn-assessment btn-assessment-danger flex items-center justify-center"
              >
                Resume Draft
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestDashboard;
